import { useState } from 'react';
import { Badge, Button, Card, LoadingState } from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import {
  recordingApi,
  useRecordingAction,
  useVideoRecordings,
} from '@/features/video-consults/api';
import type {
  RecordingStatus,
  StaffVideoSession,
  VideoRecording,
  VideoRecordingAccess,
} from '@/features/video-consults/types';
import { isApiError } from '@/lib/api';
import { klDate, klTime } from '@/pages/calendar/time';
import parity from '@/pages/ops/parity.module.css';
import { VideoError } from './VideoError';
import styles from './video.module.css';

type Tone = 'neutral' | 'info' | 'success' | 'warning' | 'danger';

const STATUS: Record<RecordingStatus, { text: string; tone: Tone }> = {
  requested: { text: 'Starting…', tone: 'info' },
  recording: { text: 'Recording', tone: 'danger' },
  processing: { text: 'Preparing the file…', tone: 'warning' },
  ingesting: { text: 'Saving the file…', tone: 'warning' },
  ready: { text: 'Ready to play', tone: 'success' },
  failed: { text: 'Failed', tone: 'danger' },
};

const FAILURE: Record<string, string> = {
  start_failed: 'The video service could not start the recording.',
  provider_failed: 'The video service reported that the recording failed.',
  provider_timeout:
    'The video service never delivered the file. It may have been lost.',
  ingest_failed: 'The file could not be saved to private storage.',
  too_large: 'The file was too large to save.',
};

function length(seconds: number | null) {
  if (seconds === null) return null;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m} min ${String(s).padStart(2, '0')} s`;
}

/**
 * RL-2158 (staging): record the consult and play the recording back. Shown only
 * to people with video_recordings:read, and hidden entirely when the server has
 * recording switched off (404 RECORDING_DISABLED). Only the doctor of the consult
 * can start or stop; playback uses a signed link of about two minutes.
 */
export function RecordingPanel({ s }: { s: StaffVideoSession }) {
  const { hasPermission } = useSession();
  const canRead = hasPermission('video_recordings:read');
  const canManage = hasPermission('video_recordings:manage') && s.mine;
  const q = useVideoRecordings(s.id, canRead);
  const start = useRecordingAction(recordingApi.start);
  const stop = useRecordingAction(recordingApi.stop);
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const act = (p: Promise<unknown>) => {
    setError(null);
    p.catch((e: unknown) => setError(e));
  };

  if (!canRead) return null;
  if (q.isLoading) return <LoadingState label="Loading recordings…" />;
  // Recording is switched off on this server, or the server predates it: nothing to show.
  if (
    isApiError(q.error) &&
    (q.error.code === 'RECORDING_DISABLED' || q.error.status === 404)
  )
    return null;
  if (q.error) {
    return (
      <Card title="Recording">
        <VideoError
          error={q.error}
          title="Could not load the recordings"
          onRetry={() => void q.refetch()}
        />
      </Card>
    );
  }
  const recordings = q.data?.recordings ?? [];
  const active = recordings.find(
    (r) => r.status === 'recording' || r.status === 'requested',
  );
  const live = s.status === 'admitted' || s.status === 'in_call';

  return (
    <Card title="Recording">
      <div className={styles.stack}>
        {error !== null && <VideoError error={error} />}
        {canManage && live && !active && (
          <section aria-label="Start a recording" className={styles.stack}>
            <label className={styles.checkbox}>
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
              />
              The patient has agreed to this consult being recorded.
            </label>
            <div>
              <Button
                variant="primary"
                disabled={!agreed}
                loading={start.isPending}
                onClick={() =>
                  act(
                    start.mutateAsync(s.id).then(() => {
                      setAgreed(false);
                    }),
                  )
                }
              >
                Start recording
              </Button>
            </div>
          </section>
        )}
        {canManage && active && (
          <div className={styles.inline}>
            <Badge tone="danger" dot>
              Recording
            </Badge>
            <Button
              loading={stop.isPending}
              onClick={() => act(stop.mutateAsync(s.id))}
            >
              Stop recording
            </Button>
          </div>
        )}
        {!canManage && active && (
          <Badge tone="danger" dot>
            Recording
          </Badge>
        )}
        {recordings.length === 0 && !active && (
          <span className={parity.sub}>
            {canManage && live
              ? 'Nothing recorded yet.'
              : 'No recording for this consult.'}
          </span>
        )}
        {recordings.map((r) => (
          <RecordingRow key={r.id} r={r} />
        ))}
      </div>
    </Card>
  );
}

function RecordingRow({ r }: { r: VideoRecording }) {
  const [link, setLink] = useState<VideoRecordingAccess | null>(null);
  const [expired, setExpired] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const st = STATUS[r.status];
  const play = () => {
    setError(null);
    setExpired(false);
    setBusy(true);
    recordingApi
      .access(r.id)
      .then(setLink)
      .catch((e: unknown) => setError(e))
      .finally(() => setBusy(false));
  };
  const video = r.contentType?.startsWith('video/');
  const meta = [
    `${klDate(r.createdAt)} ${klTime(r.createdAt)}`,
    r.mode === 'audio_only' ? 'Audio only' : 'Video',
    length(r.durationSeconds),
  ].filter(Boolean);
  return (
    <section
      aria-label={`Recording ${klTime(r.createdAt)}`}
      className={styles.stack}
    >
      <div className={styles.inline}>
        <Badge tone={st.tone}>{st.text}</Badge>
        <span className={parity.sub}>{meta.join(' · ')}</span>
      </div>
      {r.status === 'failed' && (
        <span className={styles.warn} role="status">
          {FAILURE[r.failureReason ?? ''] ?? 'The recording failed.'}
        </span>
      )}
      {error !== null && <VideoError error={error} />}
      {r.canPlay && !link && (
        <div>
          <Button size="sm" loading={busy} onClick={play}>
            Play
          </Button>
        </div>
      )}
      {r.canPlay && link && (
        <>
          {video ? (
            <video
              controls
              autoPlay
              src={link.url}
              aria-label="Recording playback"
              onError={() => setExpired(true)}
            />
          ) : (
            <audio
              controls
              autoPlay
              src={link.url}
              aria-label="Recording playback"
              onError={() => setExpired(true)}
            />
          )}
          {expired && (
            <div className={styles.warn} role="status">
              The playback link expired or could not load.{' '}
              <Button size="sm" loading={busy} onClick={play}>
                Get a new link
              </Button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
